import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Header } from "@/components/site/Header";
import { Hero } from "@/components/site/Hero";
import { HowItWorks } from "@/components/site/HowItWorks";
import { PackageBuilder } from "@/components/site/PackageBuilder";
import { BudgetBar } from "@/components/site/BudgetBar";
import { SubmissionForm } from "@/components/site/SubmissionForm";
import { Footer } from "@/components/site/Footer";
import { BUDGET_CAP } from "@/lib/constants";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LandingPage() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/services`);
        if (active) setServices(res.data);
      } catch (e) {
        if (active) toast.error("Failed to load services. Please refresh.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const selectedServices = useMemo(
    () => services.filter((s) => selectedIds.includes(s.id)),
    [services, selectedIds]
  );
  const total = useMemo(
    () => selectedServices.reduce((acc, s) => acc + s.price, 0),
    [selectedServices]
  );

  const handleToggle = useCallback(
    (service) => {
      setSelectedIds((curr) => {
        if (curr.includes(service.id)) {
          return curr.filter((id) => id !== service.id);
        }
        const newTotal = total + service.price;
        if (newTotal > BUDGET_CAP) {
          toast.warning(
            `Adding "${service.name}" would exceed the £${BUDGET_CAP.toLocaleString()} budget.`
          );
          return curr;
        }
        return [...curr, service.id];
      });
    },
    [total]
  );

  const handleRemove = useCallback((service) => {
    setSelectedIds((curr) => curr.filter((id) => id !== service.id));
  }, []);

  const scrollToBuilder = () => {
    document
      .getElementById("builder")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen bg-white text-black pb-28 md:pb-32">
      <Header onStartBuilder={scrollToBuilder} />
      <main>
        <Hero onStartBuilder={scrollToBuilder} />
        <HowItWorks />
        <PackageBuilder
          services={services}
          selectedIds={selectedIds}
          onToggle={handleToggle}
          total={total}
          loading={loading}
        />
      </main>
      <Footer />
      <BudgetBar
        total={total}
        count={selectedIds.length}
        onOpenCart={() => setOpen(true)}
      />
      <SubmissionForm
        open={open}
        onOpenChange={setOpen}
        selectedServices={selectedServices}
        onRemoveService={handleRemove}
        total={total}
        onSuccess={() => setSelectedIds([])}
      />
    </div>
  );
}
